import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../ToastContext';
import api, { settingsAPI } from '../services/api';
import ExportPanel from '../components/ExportPanel';
import { COUNTRIES, getCurrencyForCountry, COUNTRY_BY_CODE } from '../data/countries';

/* ── simple modal ────────────────────────────────────────────── */
function Modal({ title, onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 999,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.5rem',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '14px',
          padding: '1.5rem',
          width: '100%',
          maxWidth: '380px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '1rem' }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

/* ── 3-D card style ──────────────────────────────────────────── */
const card3d = {
  background: 'linear-gradient(145deg, #1e2235, #161929)',
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: '0 2px 0 rgba(0,0,0,0.4), 0 5px 16px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.07)',
  transform: 'perspective(700px) rotateX(2deg)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  borderRadius: 'var(--radius-sm)',
  padding: '1rem',
  marginBottom: '1rem',
  cursor: 'pointer',
};

const card3dHover = {
  transform: 'perspective(700px) rotateX(0deg) translateY(-4px)',
  boxShadow: '0 1px 0 rgba(0,0,0,0.3), 0 10px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
};

function Card3D({ onClick, children, style }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...card3d, ...(hovered ? card3dHover : {}), ...style }}
    >
      {children}
    </div>
  );
}

/* ── main component ──────────────────────────────────────────── */
export default function Settings() {
  const { user, business, parentCompany, billing, isEnterprise, updateBusiness, updateParentCompany, updateUser } = useAuth();
  const [emailForm, setEmailForm] = useState({ email: user?.email || '', password: '' });
  const [savingEmail, setSavingEmail] = useState(false);
  const { toast } = useToast();

  const currentCountry = COUNTRY_BY_CODE[business?.country_code] || null;
  const [selectedCountryCode, setSelectedCountryCode] = useState(business?.country_code || '');
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [changingCountry, setChangingCountry] = useState(!business?.country_code);

  const [modal, setModal] = useState(null); // 'company' | 'business' | 'password'

  const [bizName, setBizName]         = useState(business?.name || '');
  const [savingBizName, setSavingBizName] = useState(false);

  const [companyName, setCompanyName]     = useState(parentCompany?.name || '');
  const [savingCompanyName, setSavingCompanyName] = useState(false);

  const [pwForm, setPwForm]   = useState({ current: '', next: '', confirm: '' });
  const [savingPw, setSavingPw] = useState(false);

  const selectedCountry = COUNTRIES.find(c => c.code === selectedCountryCode) || null;
  const derivedCurrency = selectedCountryCode ? getCurrencyForCountry(selectedCountryCode) : '';
  const currencyChanged = derivedCurrency && derivedCurrency !== business?.currency;

  const closeModal = () => setModal(null);

  const handleSaveBizName = async () => {
    const trimmed = bizName.trim();
    if (!trimmed || trimmed === business?.name) return;
    setSavingBizName(true);
    try {
      const { data } = await api.patch('/settings/business-name', { name: trimmed });
      if (data.success) { updateBusiness({ name: data.name }); closeModal(); toast('Business name updated', 'success'); }
      else toast(data.error || 'Failed', 'error');
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
    finally { setSavingBizName(false); }
  };

  const handleSaveCompanyName = async () => {
    const trimmed = companyName.trim();
    if (!trimmed || trimmed === parentCompany?.name) return;
    setSavingCompanyName(true);
    try {
      const { data } = await api.patch('/settings/company-name', { name: trimmed });
      if (data.success) { updateParentCompany({ name: data.name }); closeModal(); toast('Company name updated', 'success'); }
      else toast(data.error || 'Failed', 'error');
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
    finally { setSavingCompanyName(false); }
  };

  const handleSaveCurrency = async () => {
    if (!selectedCountryCode || !derivedCurrency) return;
    setSavingCurrency(true);
    try {
      const { data } = await api.patch('/settings/currency', { currency: derivedCurrency, country_code: selectedCountryCode });
      if (data.success) { updateBusiness({ currency: data.currency, country_code: data.country_code }); setChangingCountry(false); toast('Currency updated', 'success'); }
      else toast(data.error || 'Failed', 'error');
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
    finally { setSavingCurrency(false); }
  };

  const handleChangePassword = async () => {
    if (pwForm.next !== pwForm.confirm) { toast('Passwords do not match', 'error'); return; }
    if (pwForm.next.length < 8) { toast('Min 8 characters', 'error'); return; }
    setSavingPw(true);
    try {
      const { data } = await api.patch('/settings/password', { currentPassword: pwForm.current, newPassword: pwForm.next });
      if (data.success) {
        // The change signs out every other session; the server set a fresh
        // session cookie for this one on the response.
        setPwForm({ current: '', next: '', confirm: '' }); closeModal(); toast('Password updated', 'success');
      }
      else toast(data.error || 'Failed', 'error');
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
    finally { setSavingPw(false); }
  };

  // A recovery email gives a phone-registered owner a second way to reset a
  // forgotten password (the first is an SMS code to their mobile number).
  const handleSaveEmail = async () => {
    setSavingEmail(true);
    try {
      const { data } = await settingsAPI.updateEmail({ email: emailForm.email.trim(), password: emailForm.password });
      updateUser({ email: data.email });
      setEmailForm(f => ({ ...f, password: '' }));
      closeModal();
      toast('Recovery email saved', 'success');
    } catch (err) { toast(err.response?.data?.error || 'Failed to save email', 'error'); }
    finally { setSavingEmail(false); }
  };

  const isOwner = user?.role === 'owner';

  return (
    <div className="page">
      <h1 className="page-title">Settings</h1>

      {/* Account card */}
      <Card3D onClick={isOwner ? () => setModal('password') : undefined} style={{ cursor: isOwner ? 'pointer' : 'default' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>Account</div>
        <div style={{ fontWeight: 700, fontSize: '1rem' }}>{user?.name}</div>
        <div className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.2rem' }}>{user?.email || (user?.phone ? `+${user.phone}` : '')}</div>
        <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.2rem', textTransform: 'capitalize' }}>Role: {user?.role}</div>
        {isOwner && <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.4rem' }}>Tap to change your password</div>}
      </Card3D>

      {/* Password recovery — reset codes go by SMS to the mobile number and/or
          by email. An email adds a second way back in if the phone is lost. */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>
          Password recovery
        </div>
        {user?.phone && (
          <div style={{ fontSize: '0.85rem' }}>Reset codes by SMS to <strong>+{user.phone}</strong></div>
        )}
        {user?.email ? (
          <div style={{ fontSize: '0.85rem' }}>Reset codes by email to <strong>{user.email}</strong></div>
        ) : (
          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
            Add an email as a second way to reset your password if you lose your phone.
          </div>
        )}
        <button className="btn-ghost" style={{ marginTop: '0.6rem', padding: '0.35rem 0.8rem', fontSize: '0.8rem' }}
          onClick={() => { setEmailForm({ email: user?.email || '', password: '' }); setModal('email'); }}>
          {user?.email ? 'Change email' : 'Add recovery email'}
        </button>
      </div>

      {/* Company card — enterprise only. Starter/Pro have a 1:1 company↔business,
          so the single card below (relabelled "Company / Business") covers both. */}
      {isEnterprise && parentCompany && (
        <Card3D onClick={isOwner && isEnterprise ? () => { setCompanyName(parentCompany.name); setModal('company'); } : undefined}
          style={{ cursor: isOwner && isEnterprise ? 'pointer' : 'default' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>Company</div>
          <div style={{ fontWeight: 700, fontSize: '1rem' }}>{parentCompany.name}</div>
          <div className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
            Currency: <strong style={{ color: 'var(--color-text)' }}>{parentCompany.currency}</strong>
          </div>
        </Card3D>
      )}

      {/* Subscription card — enterprise owners only */}
      {isEnterprise && isOwner && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>
            Subscription
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#c084fc' }}>Enterprise</div>
              {billing?.state === 'complimentary' ? (
                <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.15rem' }}>
                  Complimentary{billing.complimentary_until ? ` until ${new Date(billing.complimentary_until).toLocaleDateString('en-GB')}` : ''}
                </div>
              ) : billing?.subscription_expiry && (
                <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.15rem' }}>
                  Paid until {new Date(billing.subscription_expiry).toLocaleDateString('en-GB')} · <a href="/upgrade">Renew</a>
                </div>
              )}
            </div>
            <a
              href={`mailto:support@getshopbook.com?subject=Downgrade request — ${encodeURIComponent(parentCompany?.name || '')}&body=Hi, I'd like to downgrade my ShopBook plan from Enterprise to Pro.`}
              style={{
                fontSize: '0.78rem', color: 'var(--color-muted)',
                textDecoration: 'underline', textUnderlineOffset: '2px',
              }}
            >
              Downgrade to Pro
            </a>
          </div>
        </div>
      )}

      {/* Business card */}
      <Card3D onClick={isOwner ? () => { setBizName(business?.name || ''); setModal('business'); } : undefined}
        style={{ cursor: isOwner ? 'pointer' : 'default' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>{isEnterprise ? 'Business' : 'Company / Business'}</div>
        <div style={{ fontWeight: 700, fontSize: '1rem' }}>{business?.name}</div>
        <div className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
          Currency: <strong style={{ color: 'var(--color-text)' }}>{business?.currency}</strong>
          {currentCountry && <span> &nbsp;·&nbsp; {currentCountry.flag} {currentCountry.name}</span>}
        </div>
      </Card3D>

      {/* Currency / Country — owner only */}
      {isOwner && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Country &amp; Currency
            </div>
            {currentCountry && !changingCountry && (
              <button
                onClick={() => { setChangingCountry(true); setSelectedCountryCode(business?.country_code || ''); }}
                style={{ fontSize: '0.75rem', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Change
              </button>
            )}
          </div>

          {!changingCountry && currentCountry ? (
            /* Display mode — country already set */
            <div
              onClick={() => { setChangingCountry(true); setSelectedCountryCode(business?.country_code || ''); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.7rem 0.9rem', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border)', cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{currentCountry.flag}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{currentCountry.name}</div>
                <div className="text-muted" style={{ fontSize: '0.78rem' }}>{business?.currency} — {currentCountry.currencyName}</div>
              </div>
              <span style={{ marginLeft: 'auto', color: 'var(--color-muted)', fontSize: '0.8rem' }}>›</span>
            </div>
          ) : (
            /* Edit mode — dropdown + save */
            <>
              <div className="form-group">
                <select value={selectedCountryCode} onChange={e => setSelectedCountryCode(e.target.value)} autoFocus>
                  <option value="">— Select country —</option>
                  {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                </select>
              </div>
              {selectedCountry && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: currencyChanged ? '#0f172a' : 'var(--color-surface)',
                  border: `1px solid ${currencyChanged ? '#4338ca' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius-sm)', padding: '0.6rem 0.85rem',
                  fontSize: '0.85rem', color: currencyChanged ? '#818cf8' : 'var(--color-muted)',
                  marginBottom: '1rem',
                }}>
                  <span>Currency</span>
                  <strong>{selectedCountry.currency} — {selectedCountry.currencyName}</strong>
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn-primary" style={{ flex: 1 }} disabled={!currencyChanged || savingCurrency} onClick={handleSaveCurrency}>
                  {savingCurrency ? 'Saving…' : currencyChanged ? `Switch to ${derivedCurrency}` : 'No changes'}
                </button>
                {currentCountry && (
                  <button
                    onClick={() => { setChangingCountry(false); setSelectedCountryCode(business?.country_code || ''); }}
                    style={{ padding: '0 1rem', background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--color-muted)' }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {isOwner && <ExportPanel />}

      <p className="text-muted" style={{ fontSize: '0.8rem', textAlign: 'center', marginBottom: '1.5rem' }}>
        <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> ·{' '}
        <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
      </p>

      {/* ── Modals ────────────────────────────────────────────── */}

      {modal === 'email' && (
        <Modal title={user?.email ? 'Change recovery email' : 'Add recovery email'} onClose={closeModal}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '1rem' }}>
            <input type="email" placeholder="you@example.com" value={emailForm.email} autoFocus
              onChange={e => setEmailForm(f => ({ ...f, email: e.target.value }))} style={{ width: '100%' }} />
            <input type="password" placeholder="Your current password" value={emailForm.password}
              onChange={e => setEmailForm(f => ({ ...f, password: e.target.value }))} style={{ width: '100%' }} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-primary" style={{ flex: 1 }}
              disabled={!emailForm.email.trim() || !emailForm.password || savingEmail} onClick={handleSaveEmail}>
              {savingEmail ? 'Saving…' : 'Save'}
            </button>
            <button onClick={closeModal} style={{ flex: 1, background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--color-muted)' }}>
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {modal === 'company' && (
        <Modal title="Edit Company Name" onClose={closeModal}>
          <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)}
            placeholder="Company name" style={{ width: '100%', marginBottom: '1rem' }} autoFocus />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-primary" style={{ flex: 1 }}
              disabled={!companyName.trim() || companyName.trim() === parentCompany?.name || savingCompanyName}
              onClick={handleSaveCompanyName}>
              {savingCompanyName ? 'Saving…' : 'Save'}
            </button>
            <button onClick={closeModal} style={{ flex: 1, background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--color-muted)' }}>
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {modal === 'business' && (
        <Modal title="Edit Business Name" onClose={closeModal}>
          <input type="text" value={bizName} onChange={e => setBizName(e.target.value)}
            placeholder="Business name" style={{ width: '100%', marginBottom: '1rem' }} autoFocus />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-primary" style={{ flex: 1 }}
              disabled={!bizName.trim() || bizName.trim() === business?.name || savingBizName}
              onClick={handleSaveBizName}>
              {savingBizName ? 'Saving…' : 'Save'}
            </button>
            <button onClick={closeModal} style={{ flex: 1, background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--color-muted)' }}>
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {modal === 'password' && (
        <Modal title="Change Password" onClose={closeModal}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '1rem' }}>
            <input type="password" placeholder="Current password" value={pwForm.current}
              onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} style={{ width: '100%' }} autoFocus />
            <input type="password" placeholder="New password (min 8 characters)" value={pwForm.next}
              onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} style={{ width: '100%' }} />
            <input type="password" placeholder="Confirm new password" value={pwForm.confirm}
              onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} style={{ width: '100%' }} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-primary" style={{ flex: 1 }}
              disabled={!pwForm.current || !pwForm.next || !pwForm.confirm || savingPw}
              onClick={handleChangePassword}>
              {savingPw ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => { closeModal(); setPwForm({ current: '', next: '', confirm: '' }); }}
              style={{ flex: 1, background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--color-muted)' }}>
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
